import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { fmtDateAU, bpsToPct, centsToInput } from "../lib/format";
import {
  ArrangementStep, CalculationStep, SettingsStep, agreementPayload, defaultAgreementForm,
} from "../components/AgreementFields";
import { History, PencilLine, CalendarPlus } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function EditAgreement() {
  const navigate = useNavigate();
  const { id } = useParams();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("correct"); // "correct" | "new"
  const [form, setForm] = useState(defaultAgreementForm());
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data } = useQuery({
    queryKey: ["practice", id],
    queryFn: async () => (await api.get(`/practices/${id}`)).data,
  });
  const current = data?.current_agreement;

  useEffect(() => {
    const a = current;
    if (a) {
      setForm({
        engagement_type: a.engagement_type || "unsure",
        calc_method: a.calc_method,
        percentage: bpsToPct(a.percentage_bps),
        hourly_rate: centsToInput(a.hourly_rate_cents),
        daily_rate: centsToInput(a.daily_rate_cents),
        fixed_amount: centsToInput(a.fixed_amount_cents),
        custom_notes: a.custom_notes || "",
        lab_treatment: a.lab_treatment,
        dentist_lab_share: bpsToPct(a.dentist_lab_share_bps),
        service_fee: bpsToPct(a.service_fee_bps),
        service_fee_basis: a.service_fee_basis || "practice_share",
        gst_treatment: a.gst_treatment || "not_registered",
        gst_model: "components",
        service_fee_gst_enabled: a.gst_model === "components" ? !!a.service_fee_gst_enabled : false,
        dentist_gst_enabled: a.gst_model === "components" ? !!a.dentist_gst_enabled : (a.gst_treatment === "plus_gst"),
        deductions: (a.deduction_categories || []).reduce((o, k) => { o[k] = true; return o; }, {}),
        payment_frequency: a.payment_frequency,
        super_arrangement: a.super_arrangement,
        gst_setting: a.gst_setting,
        // Correct mode keeps the current effective date; new-version mode starts today.
        effective_start: a.effective_start || todayISO(),
      });
    }
  }, [current]);

  const pickMode = (m) => {
    setMode(m);
    if (m === "correct") set("effective_start", current?.effective_start || todayISO());
    else set("effective_start", todayISO());
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      if (mode === "correct") {
        await api.put(`/practices/${id}/agreement/${current.id}`, agreementPayload(form));
        toast.success("Agreement details corrected. Existing payment reports are unchanged.");
      } else {
        await api.post(`/practices/${id}/agreement`, agreementPayload(form));
        toast.success("New agreement version saved. Past reports keep their original calculations.");
      }
      qc.invalidateQueries();
      navigate(`/practices/${id}`);
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const ModeCard = ({ value, icon: Icon, title, desc }) => (
    <button type="button" data-testid={`agreement-mode-${value}`} onClick={() => pickMode(value)}
      className={`flex-1 text-left rounded-2xl border p-3.5 df-pressable transition-colors ${
        mode === value ? "border-[#0D786A] bg-[#F4FBF9]" : "border-slate-200 bg-white"}`}>
      <Icon size={18} className={mode === value ? "text-[#0D786A]" : "text-slate-400"} />
      <div className="text-sm font-semibold text-slate-900 mt-1.5">{title}</div>
      <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{desc}</div>
    </button>
  );

  return (
    <AppShell>
      <PageHeader title="Edit Agreement" subtitle={data?.practice?.name} onBack={() => navigate(-1)} />
      <div className="px-5 pb-40 df-fade-in space-y-4">
        <div className="flex gap-2.5">
          <ModeCard value="correct" icon={PencilLine} title="Correct current details"
            desc="Fix a mistake in the agreement you have now. Keeps the same start date." />
          <ModeCard value="new" icon={CalendarPlus} title="Terms changed from a new date"
            desc="Your arrangement changed. Start a new version from a chosen date." />
        </div>

        <div className={`rounded-2xl border p-4 flex gap-2 text-sm ${
          mode === "correct" ? "bg-amber-50 border-amber-100 text-amber-800" : "bg-indigo-50 border-indigo-100 text-indigo-800"}`}>
          <History size={18} className="shrink-0 mt-0.5" />
          {mode === "correct" ? (
            <span>You're <b>correcting the current agreement</b>. If a payment report has already used it, Molaroo will ask you to start a new version instead so your saved reports stay exactly as they are.</span>
          ) : (
            <span>Saving creates a <b>new agreement version</b> from the effective date below. Earlier reports keep the agreement and calculation that applied at the time.</span>
          )}
        </div>

        {data?.agreements?.length > 0 && (
          <div className="df-card p-4">
            <div className="text-sm font-semibold text-slate-900 mb-2">Version history</div>
            <div className="space-y-1.5">
              {data.agreements.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-sm" data-testid={`version-${a.version}`}>
                  <span className="text-slate-600">v{a.version} · from {fmtDateAU(a.effective_start)}</span>
                  <span className="text-slate-400">{a.effective_end ? `to ${fmtDateAU(a.effective_end)}` : "current"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="df-card p-5"><ArrangementStep form={form} set={set} /></div>
        <div className="df-card p-5"><CalculationStep form={form} set={set} /></div>
        <div className="df-card p-5"><SettingsStep form={form} set={set} /></div>
      </div>

      <div className="df-actionbar">
        <Button variant="outline" onClick={() => navigate(-1)} className="flex-1 h-12 rounded-xl">Cancel</Button>
        <Button data-testid="save-agreement" onClick={save} disabled={saving}
          className="flex-1 h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          {saving ? "Saving…" : (mode === "correct" ? "Save Corrections" : "Save New Version")}
        </Button>
      </div>
    </AppShell>
  );
}
